import {
  BadgeElement,
  BadgeElementType,
  BadgeTextData,
  BadgeFieldData,
  BadgeShapeData,
  ColorValue,
} from "sharedTypes/badge"
import { colorIsValid, colorWithHash } from "utilities/colors"
import { BadgeElementFormValues } from "../Panels/EditorPanel/BadgeSidePanelForm"
import { ElementUpdate } from "../hooks/useElementTransforms"
import { DEFAULT_BORDER_STYLE, DEFAULT_TRANSFORM_STYLE } from "./badgeHelpers"

export const normalizeElementData = (
  type: BadgeElementType,
  data: BadgeElement["data"],
): BadgeElement["data"] => {
  switch (type) {
    case BadgeElementType.TEXT: {
      const textData = data as BadgeTextData

      return {
        ...textData,
        align: textData.align || "left",
        verticalAlign: textData.verticalAlign || "center",
        weight: textData.weight || 400,
        textDecoration: textData.textDecoration || "none",
        textTransform: textData.textTransform || "none",
      } as BadgeTextData
    }
    case BadgeElementType.FIELD: {
      const fieldData = data as BadgeFieldData

      return {
        ...fieldData,
        align: fieldData.align || "left",
        verticalAlign: fieldData.verticalAlign || "center",
        weight: fieldData.weight || 400,
        textDecoration: fieldData.textDecoration || "none",
        textTransform: fieldData.textTransform || "none",
      } as BadgeFieldData
    }
    case BadgeElementType.RECTANGLE:
    case BadgeElementType.OVAL: {
      const shapeData = data as BadgeShapeData

      return {
        ...shapeData,
        strokeColor: shapeData.strokeColor ?? "#000000",
        fillColor: shapeData.fillColor ?? "transparent",
        thickness: shapeData.thickness ?? 1,
        style: shapeData.style ?? "solid",
      } as BadgeShapeData
    }
    default:
      return data
  }
}

export const elementToFormValues = (element: BadgeElement): BadgeElementFormValues => {
  const normalizedData = normalizeElementData(element.type, element.data)

  return {
    selectedBlockType: element.type,
    x: element.x,
    y: element.y,
    width: element.width,
    height: element.height,
    zIndex: element.zIndex || 1,
    fill: element.fill || "transparent",
    border: element.border || DEFAULT_BORDER_STYLE,
    transforms: element.transforms || DEFAULT_TRANSFORM_STYLE,
    ...normalizedData,
  } as BadgeElementFormValues
}

export const formValuesToElementUpdate = (
  formValues: BadgeElementFormValues,
): ElementUpdate => {
  const {
    selectedBlockType,
    x,
    y,
    width,
    height,
    zIndex,
    fill,
    border,
    transforms,
    ...elementData
  } = formValues

  const updatedChanges: ElementUpdate = {}

  if (x !== undefined) updatedChanges.x = x
  if (y !== undefined) updatedChanges.y = y
  if (width !== undefined) updatedChanges.width = width
  if (height !== undefined) updatedChanges.height = height
  if (zIndex !== undefined) updatedChanges.zIndex = zIndex

  if (fill !== undefined) {
    if (fill === "transparent") {
      updatedChanges.fill = "transparent"
    } else if (colorIsValid(fill)) {
      updatedChanges.fill = colorWithHash(fill) as ColorValue
    }
  }

  if (border !== undefined) {
    updatedChanges.border = {
      color: border.color || "#000000",
      width: border.width ?? 0,
      style: border.style || "solid",
    }
  }

  if (transforms !== undefined) {
    updatedChanges.transforms = {
      rotate: transforms.rotate ?? 0,
      flipHorizontal: transforms.flipHorizontal ?? false,
      flipVertical: transforms.flipVertical ?? false,
    }
  }

  if (Object.keys(elementData).length > 0) {
    Object.assign(updatedChanges, elementData)
  }

  return updatedChanges
}
